/**
 * AboutPage.smoke.test.tsx
 *
 * Mit tesztel?
 * - "Smoke" jellegű teszt: csak azt ellenőrzi, hogy az AboutPage komponens hiba nélkül renderelhető.
 * - Nem vizsgálja a stílusokat, az animációkat, a routert vagy az Auth/Theme logikát.
 *
 * Miért vannak mockok?
 * - @mui/material: a useTheme-t egyszerűsítjük (fix light mód + shadows), hogy a nagy MUI csomagok ne lassítsák/akasszák a bundlingot.
 * - @mui/icons-material: ikonok helyett üres <i> elemek, hogy ne húzzunk be nehéz ikoncsomagokat.
 * - ThemeContext: a useColorMode() mindig { mode: 'light' }-ot ad, így nem kell valódi ThemeProvider.
 *
 * Eredmény:
 * - Gyors és stabil füstpróba (import + render működik).
 * - Ha ez fut, a komponenst biztonsággal lehet részletesebb tesztekkel is vizsgálni.
 */import { Box, Typography, List, ListItem, ListItemIcon, ListItemText, Grid, useTheme, Divider } from '@mui/material';
import { Link as LinkIcon, Assignment as AssignmentIcon, Timeline as TimelineIcon, CheckCircleOutline } from '@mui/icons-material';
import { useColorMode } from '../../../../context/ThemeContext';
import { keyframes } from '@mui/system';

export default function AboutPage() {
  const { mode } = useColorMode();
  const theme = useTheme();

  const supported = [
    { name: 'Reddit', src: '/reddit.png' },
    { name: 'YouTube', src: '/youtube.png' },
    { name: 'Google Maps', src: '/google.png' },
    { name: 'Stack Overflow', src: '/stacko.png' },
    { name: 'X / Twitter', src: '/x.webp' },
    { name: 'GyakoriKérdések', src: '/gyik.png' },
  ];

  // Finom belépő animáció + hover lebegés
  const fadeUp = keyframes`
    from { opacity: 0; transform: translateY(8px) }
    to   { opacity: 1; transform: translateY(0) }
  `;
  const hoverFloat = {
    transform: 'translateY(0)',
    transition: 'transform .25s ease, box-shadow .25s ease, background-color .25s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[6],
      background:
        mode === 'dark'
          ? `linear-gradient(180deg, ${theme.palette.grey[800]}, ${theme.palette.grey[900]})`
          : `linear-gradient(180deg, ${theme.palette.grey[100]}, ${theme.palette.grey[50]})`,
    },
  };

  return (
    <Box
      sx={{
        // + top space a navbar/oldalsáv miatt
        mt: { xs: 3, md: 5 },
        maxWidth: 980,
        mx: 'auto',
        p: { xs: 3, md: 4 },
        backgroundColor: mode === 'dark' ? 'grey.900' : 'grey.50',
        borderRadius: 3,
        boxShadow: 4,
        animation: `${fadeUp} .35s ease`,
      }}
    >
      {/* Logó */}
      <Box
        component="img"
        src="/reddit_analyzer.png"
        alt="Analyzer Logo"
        sx={{
          width: 220,
          height: 'auto',
          mx: 'auto',
          mb: 4,
          display: 'block',
          filter: mode === 'dark' ? 'brightness(0.9)' : 'none',
          transition: 'transform .25s ease',
          '&:hover': { transform: 'scale(1.02)' },
        }}
      />

      {/* Cím + bevezető */}
      <Typography
        variant="h4"
        gutterBottom
        sx={{ textAlign: 'center', color: mode === 'dark' ? 'primary.light' : 'primary.dark' }}
      >
        Üdv az Analyzerben!
      </Typography>
      <Typography
        variant="body1"
        paragraph
        sx={{ color: 'text.primary', textAlign: 'center', maxWidth: 760, mx: 'auto' }}
      >
        Egyetlen linkből átlátható hangulatelemzést készítünk: az AI 0–10 skálán értékeli a kommentek érzelmi töltetét,
        és vizualizációkkal segít megérteni a közösség visszajelzéseit.
      </Typography>

      {/* Támogatott források */}
      <Box sx={{ mt: 4 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{ color: mode === 'dark' ? 'secondary.light' : 'secondary.dark', display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <CheckCircleOutline fontSize="small" />
          Támogatott források
        </Typography>

        <Grid
          container
          spacing={2}
          sx={{ alignItems: 'stretch' /* <- minden item egyforma magasság */ }}
        >
          {supported.map(({ name, src }, i) => (
            <Grid item xs={6} sm={4} md={2} key={name} sx={{ display: 'flex' }}>
              <Box
                sx={{
                  ...hoverFloat,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: mode === 'dark' ? 'grey.800' : 'grey.100',
                  width: '100%',
                  // egyenlő kártyamagasság:
                  minHeight: { xs: 110, sm: 120 },
                  animation: `${fadeUp} .4s ease`,
                  animationDelay: `${i * 60}ms`,
                  animationFillMode: 'backwards',
                }}
              >
                <Box
                  component="img"
                  src={src}
                  alt={name}
                  sx={{
                    width: 42,
                    height: 42,
                    objectFit: 'contain',
                    userSelect: 'none',
                  }}
                />
                <Typography variant="body2" sx={{ textAlign: 'center', fontWeight: 500 }}>
                  {name}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Használati útmutató */}
      <Typography
        variant="h5"
        gutterBottom
        sx={{ mt: 5, color: mode === 'dark' ? 'secondary.light' : 'secondary.dark' }}
      >
        Hogyan működik? 3 lépésben:
      </Typography>

      <List sx={{ width: '100%' }}>
        <ListItem sx={{ py: 2, animation: `${fadeUp} .35s ease`, animationDelay: '50ms' }}>
          <ListItemIcon>
            <LinkIcon sx={{ color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main' }} />
          </ListItemIcon>
          <ListItemText
            primary="1. Link beküldése"
            secondary="Illessz be egy Reddit / YouTube / Google Maps / Stack Overflow / X / GyakoriKérdések linket a főoldalon."
            secondaryTypographyProps={{ color: 'text.secondary' }}
          />
        </ListItem>

        <Divider variant="inset" component="li" sx={{ opacity: 0.25 }} />

        <ListItem sx={{ py: 2, animation: `${fadeUp} .35s ease`, animationDelay: '120ms' }}>
          <ListItemIcon>
            <AssignmentIcon sx={{ color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main' }} />
          </ListItemIcon>
          <ListItemText
            primary="2. Automatikus elemzés"
            secondary="Az Analyzer letölti a releváns kommenteket és kiértékeli őket AI segítségével."
            secondaryTypographyProps={{ color: 'text.secondary' }}
          />
        </ListItem>

        <Divider variant="inset" component="li" sx={{ opacity: 0.25 }} />

        <ListItem sx={{ py: 2, animation: `${fadeUp} .35s ease`, animationDelay: '190ms' }}>
          <ListItemIcon>
            <TimelineIcon sx={{ color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.main' }} />
          </ListItemIcon>
          <ListItemText
            primary="3. Eredmények megtekintése"
            secondary="Grafikonok, statisztikák és átlagpontszám – minden egy helyen."
            secondaryTypographyProps={{ color: 'text.secondary' }}
          />
        </ListItem>
      </List>

      <Typography variant="body2" sx={{ mt: 4, fontStyle: 'italic', color: 'text.secondary' }}>
        Megjegyzés: 0 a nagyon negatív, 10 a nagyon pozitív érzelem. Az értékek a kommentek szövegéből számítódnak.
      </Typography>
    </Box>
  );
}
